import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@fragments';
import { useArtEditorContext } from '../../../context';
import { createTextLayer, inkFor } from '../../../../model/factory';
import { cssFamily, fontEntry } from '../../../../model/fonts';
import { readableInk } from '../../../../model/colors';

// Text to add: the three hierarchy presets in the brand faces, then a few ready styles
// that are common on posts (a marker highlight, a label, a quote, a big number).
const STYLES = [
  { id: 'marker', preset: 'subheading', textKey: 'styleMarkerText', style: { highlight: { mode: 'lines', padding: 14, radius: 8 } }, fill: 'accent' },
  { id: 'label', preset: 'body', textKey: 'styleLabelText', style: { font_size: 28, font_weight: 700, transform: 'uppercase', letter_spacing: 0.08, highlight: { mode: 'box', padding: 18, radius: 999 } }, fill: 'primary', width: 360 },
  { id: 'quote', preset: 'subheading', textKey: 'styleQuoteText', style: { italic: true, font_weight: 500, font_family: 'Playfair Display' } },
  { id: 'stat', preset: 'heading', textKey: 'styleStatText', style: { font_size: 220, font_weight: 800, line_height: 1 }, width: 600, binding: 'stat' },
];

export default function TextPanel({ onClose }) {
  const { t } = useTranslation('artEditor');
  const { editor, brandKit, readOnly } = useArtEditorContext();
  const doc = editor.document;
  if (!doc) return null;
  const fonts = brandKit?.fonts || null;
  const ink = inkFor(editor.page);
  const brandColor = role => brandKit?.colors?.find(color => color.role === role)?.hex;
  const add = (preset, text, extra = {}) => {
    const layer = createTextLayer(doc, { preset, text, fonts, color: ink, ...extra });
    editor.add(layer);
    onClose?.();
  };
  const addStyle = item => {
    const fill = brandColor(item.fill) || (item.fill === 'accent' ? '#ffc83d' : '#1f4d3a');
    const style = item.style.highlight ? { ...item.style, highlight: { ...item.style.highlight, color: fill }, color: readableInk(fill) } : item.style;
    add(item.preset, t(item.textKey), { style, width: item.width, binding: item.binding });
  };
  const face = role => cssFamily(fontEntry((fonts?.[role] || (role === 'subheading' ? fonts?.heading : null) || {}).family).family);
  return <>
    <section className="art-panel-section">
      <Text.caption contrast={6}>{t('textAddHint')}</Text.caption>
      <button type="button" className="art-text-add is-heading" disabled={readOnly} style={{ '--art-font': face('heading') }} onClick={() => add('heading', t('newHeading'))}>
        <span>{t('addHeading')}</span>
      </button>
      <button type="button" className="art-text-add is-subheading" disabled={readOnly} style={{ '--art-font': face('subheading') }} onClick={() => add('subheading', t('newSubheading'))}>
        <span>{t('addSubheading')}</span>
      </button>
      <button type="button" className="art-text-add is-body" disabled={readOnly} style={{ '--art-font': face('body') }} onClick={() => add('body', t('newBody'))}>
        <span>{t('addBody')}</span>
      </button>
    </section>
    <section className="art-panel-section">
      <Text.subheading contrast={6}>{t('textStyles')}</Text.subheading>
      <div className="art-tiles is-wide">
        {STYLES.map(item => <button key={item.id} type="button" className={`art-tile art-text-style is-${item.id}`} disabled={readOnly} onClick={() => addStyle(item)}>
          <span>{t(`style_${item.id}`)}</span>
        </button>)}
      </div>
    </section>
  </>;
}
